import { useState } from 'react';
import { useStudentData } from '@/hooks/useStudentData';
import { formatDate } from '@/lib/date';

const typeMeta: Record<string, { label: string; color: string; icon: string }> = {
  pdf: { label: 'PDF', color: 'red', icon: 'ri-file-pdf-line' },
  video: { label: 'Video', color: 'emerald', icon: 'ri-video-line' },
  sunum: { label: 'Sunum', color: 'amber', icon: 'ri-slideshow-line' },
  word: { label: 'Word', color: 'blue', icon: 'ri-file-word-line' },
  excel: { label: 'Excel', color: 'emerald', icon: 'ri-file-excel-line' },
  diger: { label: 'Diğer', color: 'gray', icon: 'ri-file-line' },
};

export default function StudentDokumanlar() {
  const { group, documents, loading, error, refetch } = useStudentData();
  const [search, setSearch] = useState('');

  const filtered = documents.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (d.title ?? '').toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Dokümanlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Dokümanlar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={refetch} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Dokümanlarım</h1>
          <p className="mt-1 text-sm text-foreground-600">Ders notları, PDF'ler ve diğer kaynaklara buradan ulaşabilirsiniz.</p>
        </div>
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir grup bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Ders içerikleriniz tanımlandığında burada görünecek.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Dokümanlarım</h1>
        <p className="mt-1 text-sm text-foreground-600">Ders notları, PDF'ler ve diğer kaynaklara buradan ulaşabilirsiniz.</p>
      </div>

      <div className="relative max-w-md">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Doküman ara..."
          className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 ? (
          <div className="col-span-full rounded-xl border border-background-200 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-semibold text-foreground-700">Henüz size atanmış bir doküman bulunmuyor.</p>
            <p className="mt-1 text-xs text-foreground-500">Öğretmeniniz doküman eklediğinde burada görünecek.</p>
          </div>
        ) : (
          filtered.map((d) => {
            const t = typeMeta[d.type ?? 'diger'] ?? typeMeta.diger;
            return (
              <div
                key={d.id}
                className="flex flex-col rounded-xl border border-background-200 bg-white p-5 transition hover:shadow-md"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg`} style={{ backgroundColor: t.color === 'red' ? '#fee2e2' : t.color === 'emerald' ? '#d1fae5' : t.color === 'amber' ? '#fef3c7' : t.color === 'blue' ? '#dbeafe' : '#f3f4f6', color: t.color === 'red' ? '#ef4444' : t.color === 'emerald' ? '#10b981' : t.color === 'amber' ? '#f59e0b' : t.color === 'blue' ? '#3b82f6' : '#9ca3af' }}>
                    <i className={`${t.icon} text-xl`} />
                  </div>
                </div>

                <h3 className="font-semibold text-foreground-950 text-sm leading-snug mb-1">{d.title}</h3>
                {d.description && <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{d.description}</p>}

                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  {d.size && <span>{d.size}</span>}
                  <span>{d.upload_date ? formatDate(d.upload_date) : '—'}</span>
                  <span className="flex items-center gap-1"><i className="ri-download-line" /> {d.downloads} indirme</span>
                </div>

                <div className="mt-auto">
                  {d.file_url ? (
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600"
                    >
                      <i className="ri-external-link-line" /> {d.downloadable ? 'İndir / Aç' : 'Aç'}
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-lg bg-background-100 py-2.5 text-sm font-semibold text-foreground-400 cursor-not-allowed"
                    >
                      Link Yok
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}